import React, { useCallback, useEffect, useState } from 'react';
import ApiService from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Child } from '../types';
import PhoneInput from './PhoneInput';
import './ConnectionsScreen.css';
import './ClassRepSection.css';

const apiSvc = ApiService.getInstance();

/**
 * Classes.
 *
 * A parent who reps their child's class builds the class here once — every
 * child in it, with the parent's email — and from then on addresses the whole
 * class in one go: invite them to an activity, or remind them one is coming up.
 *
 * Adding a child creates the ordinary connection request behind the scenes, so
 * Being in a class is not a friendship: adding a child asks their parent to
 * accept the class itself, and creates no connection between the children. A
 * parent who isn't on Cluubz yet gets a skeleton account that fills in when
 * they join.
 */

interface ClassMember {
  uuid: string;
  child_uuid: string;
  parent_uuid: string;
  child_name: string;
  parent_name: string;
  parent_email: string;
  parent_is_skeleton: boolean;
  status: string;
}

interface ClassRep {
  uuid: string;
  parent_uuid: string;
  parent_name: string;
  parent_email: string;
  child_name?: string;
  status: string;
  is_owner: boolean;
}

interface ClassGroup {
  uuid: string;
  name: string;
  school_name?: string;
  year_group?: string;
  owner_child_uuid?: string;
  owner_child_name?: string;
  my_child_uuid?: string;
  my_child_name?: string;
  is_owner?: boolean;
  members: ClassMember[];
  reps: ClassRep[];
}

interface ClassRepSectionProps {
  myChildren: Child[];
  collapsed: boolean;
  onToggle: () => void;
}

// A member is only reachable by push once their parent has a real account and
// has accepted; everything else is still in flight.
const formatActivityDate = (value: string) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('en-GB');
};

const memberStatus = (member: ClassMember): { label: string; tone: string } => {
  if (member.status === 'requested') return { label: 'Asked to join', tone: 'pending' };
  if (member.parent_is_skeleton) return { label: 'Not on Cluubz yet', tone: 'pending' };
  if (member.status === 'accepted') return { label: 'In the class', tone: 'connected' };
  if (member.status === 'declined') return { label: 'Declined', tone: 'declined' };
  return { label: 'Awaiting reply', tone: 'pending' };
};

const ClassRepSection: React.FC<ClassRepSectionProps> = ({ myChildren, collapsed, onToggle }) => {
  const { user } = useAuth();
  const myParentUuid = user?.uuid;
  const [classes, setClasses] = useState<ClassGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [showCreate, setShowCreate] = useState(false);
  const [newClassName, setNewClassName] = useState('');
  const [newSchoolName, setNewSchoolName] = useState('');
  const [newYearGroup, setNewYearGroup] = useState('');
  const [newOwnerChildUuid, setNewOwnerChildUuid] = useState('');

  const [addMemberFor, setAddMemberFor] = useState<ClassGroup | null>(null);
  // Adding a co-rep uses the same search; the mode says what a result means.
  const [addMode, setAddMode] = useState<'member' | 'rep'>('member');

  // Finding your own child's class, rather than running one.
  const [showFindClass, setShowFindClass] = useState(false);
  const [findSchool, setFindSchool] = useState('');
  const [findClassName, setFindClassName] = useState('');
  const [findChildUuid, setFindChildUuid] = useState('');
  const [findResults, setFindResults] = useState<any[] | null>(null);
  // Classes my own children are in, which is most parents' whole relationship
  // with this feature.
  const [memberships, setMemberships] = useState<any[]>([]);
  const [finding, setFinding] = useState(false);
  const [searchMethod, setSearchMethod] = useState<'email' | 'phone'>('email');
  const [searchEmail, setSearchEmail] = useState('');
  const [searchPhone, setSearchPhone] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  // Set once a search has come back empty, which is what unlocks the
  // hold-a-place form — we never ask for names before we've looked.
  const [searchedFor, setSearchedFor] = useState<string | null>(null);
  const [memberChildName, setMemberChildName] = useState('');
  const [memberParentName, setMemberParentName] = useState('');

  // The activity picker doubles as invite and remind — same class, same list of
  // the rep child's upcoming activities, different action.
  const [activityFor, setActivityFor] = useState<{ group: ClassGroup; mode: 'invite' | 'remind' } | null>(null);
  const [activities, setActivities] = useState<any[]>([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [selectedActivityUuid, setSelectedActivityUuid] = useState('');
  const [activityMessage, setActivityMessage] = useState('');

  const loadClasses = useCallback(async (): Promise<ClassGroup[]> => {
    setLoading(true);
    try {
      const [response, mine] = await Promise.all([
        apiSvc.getClasses(),
        apiSvc.getClassMemberships()
      ]);
      setMemberships(mine.success && mine.data ? mine.data : []);
      if (response.success && response.data) {
        const loaded = response.data as ClassGroup[];
        setClasses(loaded);
        // Keep whichever class a modal is showing pointed at the fresh copy.
        setAddMemberFor(prev => (prev ? loaded.find(g => g.uuid === prev.uuid) || null : null));
        return loaded;
      }
      setError(response.error || 'Failed to load your classes');
    } catch (err) {
      setError('Failed to load your classes');
    } finally {
      setLoading(false);
    }
    return [];
  }, []);

  useEffect(() => {
    if (!collapsed && classes.length === 0 && !loading) {
      loadClasses();
    }
    // Only refetch when the section is opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapsed]);

  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setNotice(null), 6000);
    return () => clearTimeout(timer);
  }, [notice]);

  const handleCreateClass = async () => {
    if (!newClassName.trim()) {
      setError('Give the class a name');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const response = await apiSvc.createClass({
        name: newClassName.trim(),
        school_name: newSchoolName.trim() || undefined,
        year_group: newYearGroup.trim() || undefined,
        owner_child_uuid: newOwnerChildUuid || undefined
      });
      if (response.success) {
        setShowCreate(false);
        setNewClassName('');
        setNewSchoolName('');
        setNewYearGroup('');
        setNewOwnerChildUuid('');
        await loadClasses();
      } else {
        setError(response.error || 'Failed to create the class');
      }
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteClass = async (group: ClassGroup) => {
    if (!window.confirm(`Delete "${group.name}"? Every rep loses it, and the children in it are no longer grouped.`)) return;
    setBusy(true);
    try {
      const response = await apiSvc.deleteClass(group.uuid);
      if (response.success) {
        await loadClasses();
      } else {
        setError(response.error || 'Failed to delete the class');
      }
    } finally {
      setBusy(false);
    }
  };

  // A search that found nobody, on the one method that can hold a place.
  const showHoldAPlace = addMode === 'member' && !!searchedFor && searchResults.length === 0 && searchMethod === 'email';

  const openFindClass = () => {
    const child = myChildren[0];
    // Start from what we already know about the child, so most parents only
    // have to confirm rather than type.
    setFindChildUuid(child?.uuid || '');
    setFindSchool((child as any)?.school || '');
    setFindClassName((child as any)?.grade || '');
    setFindResults(null);
    setError(null);
    setShowFindClass(true);
  };

  const handleFindClasses = async () => {
    if (!findSchool.trim() && !findClassName.trim()) {
      setError("Enter your child's school or class name");
      return;
    }
    setFinding(true);
    setError(null);
    try {
      const response = await apiSvc.searchClasses(findSchool.trim(), findClassName.trim());
      if (response.success) {
        setFindResults(response.data || []);
      } else {
        setError(response.error || 'Search failed');
      }
    } catch (err) {
      setError('Search failed');
    } finally {
      setFinding(false);
    }
  };

  // An invitation found through the search is answered on the spot, rather
  // than sending the parent off to look for a notification.
  const handleAcceptFoundInvitation = async (group: any) => {
    if (!group.my_member_uuid) return;
    setBusy(true);
    setError(null);
    try {
      const response = await apiSvc.respondToClassInvitation(group.my_member_uuid, 'accept');
      if (response.success) {
        setNotice(`Joined ${group.name}.`);
        setShowFindClass(false);
        await loadClasses();
      } else {
        setError(response.error || 'Failed to join that class');
      }
    } finally {
      setBusy(false);
    }
  };

  const handleRequestToJoin = async (group: any) => {
    if (!findChildUuid) {
      setError('Choose which of your children is in the class');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const response = await apiSvc.requestToJoinClass(group.uuid, findChildUuid);
      if (response.success) {
        const repNames = (group.reps || []).join(' and ');
        setNotice(`Asked ${repNames || 'the rep'} to join ${group.name}. You'll hear back when they answer.`);
        setShowFindClass(false);
      } else {
        setError(response.error || 'Failed to ask to join');
      }
    } finally {
      setBusy(false);
    }
  };

  const resetMemberSearch = () => {
    setSearchResults([]);
    setSearchedFor(null);
    setMemberChildName('');
    setMemberParentName('');
  };

  const closeAddMember = () => {
    setAddMemberFor(null);
    setSearchEmail('');
    setSearchPhone('');
    resetMemberSearch();
  };

  const handleSearchParent = async () => {
    const searchValue = searchMethod === 'email' ? searchEmail.trim() : searchPhone.trim();
    if (searchValue.length < 3) {
      setError('Enter at least 3 characters to search');
      return;
    }

    setSearching(true);
    setError(null);
    resetMemberSearch();
    try {
      const response = await apiSvc.searchParent(searchValue, searchMethod);
      if (response.success) {
        setSearchResults(response.data || []);
        setSearchedFor(searchValue);
      } else {
        setError(response.error || 'Search failed');
      }
    } catch (err) {
      setError('Search failed');
    } finally {
      setSearching(false);
    }
  };

  // Adding a child found by search: the child already exists, so we send its
  // UUID and the backend takes the parent from it.
  const handleAddRep = async (parentUuid: string, parentName: string) => {
    if (!addMemberFor) return;
    setBusy(true);
    setError(null);
    try {
      const response = await apiSvc.addClassRep(addMemberFor.uuid, { parent_uuid: parentUuid });
      if (response.success) {
        setNotice(`Asked ${parentName} to help rep ${addMemberFor.name}.`);
        setSearchEmail('');
        setSearchPhone('');
        resetMemberSearch();
        await loadClasses();
      } else {
        setError(response.error || 'Failed to ask that parent');
      }
    } finally {
      setBusy(false);
    }
  };

  const handleAddFoundChild = async (childUuid: string, childName: string) => {
    if (!addMemberFor) return;
    setBusy(true);
    setError(null);
    try {
      const response = await apiSvc.addClassMember(addMemberFor.uuid, { child_uuid: childUuid });
      if (response.success) {
        setNotice(`${childName} added to ${addMemberFor.name} — a connection request is on its way to their parent.`);
        setSearchEmail('');
        setSearchPhone('');
        resetMemberSearch();
        await loadClasses();
      } else {
        setError(response.error || 'Failed to add that child');
      }
    } finally {
      setBusy(false);
    }
  };

  // Nobody matched the search, so hold a place: a skeleton account that fills
  // in when the parent joins. That needs an address to reach them on, which is
  // why this path is email-only.
  const handleAddNewChild = async () => {
    if (!addMemberFor) return;
    if (!memberChildName.trim()) {
      setError("Enter the child's name");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const response = await apiSvc.addClassMember(addMemberFor.uuid, {
        child_name: memberChildName.trim(),
        parent_name: memberParentName.trim() || undefined,
        parent_email: searchEmail.trim()
      });
      if (response.success) {
        setNotice(`${memberChildName.trim()} added to ${addMemberFor.name} — their parent isn't on Cluubz yet, so they'll be connected when they join.`);
        setSearchEmail('');
        setSearchPhone('');
        resetMemberSearch();
        await loadClasses();
      } else {
        setError(response.error || 'Failed to add that child');
      }
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveRep = async (group: ClassGroup, rep: ClassRep) => {
    const label = rep.parent_uuid === myParentUuid ? `Step down as a rep of ${group.name}?` : `Remove ${rep.parent_name} as a rep of ${group.name}?`;
    if (!window.confirm(label)) return;
    setBusy(true);
    try {
      const response = await apiSvc.removeClassRep(group.uuid, rep.uuid);
      if (response.success) {
        await loadClasses();
      } else {
        setError(response.error || 'Failed to remove that rep');
      }
    } finally {
      setBusy(false);
    }
  };

  const handleJoinRequestResponse = async (group: ClassGroup, member: ClassMember, action: 'accept' | 'decline') => {
    setBusy(true);
    setError(null);
    try {
      const response = await apiSvc.respondToJoinRequest(group.uuid, member.uuid, action);
      if (response.success) {
        setNotice(action === 'accept'
          ? `${member.child_name} is now in ${group.name}.`
          : `Turned down the request to join ${member.child_name}.`);
        await loadClasses();
      } else {
        setError(response.error || 'Failed to answer that request');
      }
    } finally {
      setBusy(false);
    }
  };

  const handleLeaveClass = async (membership: any) => {
    if (!window.confirm(`Take ${membership.child_name} out of ${membership.class_name}?`)) return;
    setBusy(true);
    setError(null);
    try {
      const response = await apiSvc.removeClassMember(membership.class_uuid, membership.member_uuid);
      if (response.success) {
        setNotice(`${membership.child_name} is no longer in ${membership.class_name}.`);
        await loadClasses();
      } else {
        setError(response.error || 'Failed to leave that class');
      }
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveMember = async (group: ClassGroup, member: ClassMember) => {
    if (!window.confirm(`Remove ${member.child_name} from ${group.name}?`)) return;
    setBusy(true);
    try {
      const response = await apiSvc.removeClassMember(group.uuid, member.uuid);
      if (response.success) {
        await loadClasses();
      } else {
        setError(response.error || 'Failed to remove that child');
      }
    } finally {
      setBusy(false);
    }
  };

  const openActivityPicker = async (group: ClassGroup, mode: 'invite' | 'remind') => {
    const repChildUuid = group.my_child_uuid || group.owner_child_uuid;
    if (!repChildUuid) {
      setError('Set which of your children this class belongs to before inviting anyone.');
      return;
    }
    setActivityFor({ group, mode });
    setSelectedActivityUuid('');
    setActivityMessage('');
    setLoadingActivities(true);
    try {
      const response = await apiSvc.getActivities(repChildUuid);
      const today = new Date().toISOString().split('T')[0];
      const upcoming = (response.data || [])
        .map((a: any) => ({ ...a, uuid: a.activity_uuid || a.uuid }))
        .filter((a: any) => a.uuid && (!a.start_date || String(a.start_date) >= today))
        .sort((a: any, b: any) => String(a.start_date).localeCompare(String(b.start_date)));
      setActivities(upcoming);
    } catch (err) {
      setError('Failed to load activities');
    } finally {
      setLoadingActivities(false);
    }
  };

  const handleSendToClass = async () => {
    if (!activityFor || !selectedActivityUuid) {
      setError('Pick an activity first');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { group, mode } = activityFor;
      const response = mode === 'invite'
        ? await apiSvc.inviteClassToActivity(group.uuid, selectedActivityUuid, activityMessage.trim() || undefined)
        : await apiSvc.remindClassAboutActivity(group.uuid, selectedActivityUuid, activityMessage.trim() || undefined);

      if (response.success) {
        const data = response.data || {};
        const plural = (n: number) => (n === 1 ? '' : 's');
        const extras = [
          data.queued_count ? `queued ${data.queued_count} who haven't joined Cluubz yet` : '',
          data.awaiting_count ? `held ${data.awaiting_count} who haven't accepted the class yet` : ''
        ].filter(Boolean);
        setNotice([
          mode === 'invite'
            ? `Invited ${data.invited_count} parent${plural(data.invited_count)}`
            : [
                `Reminded ${data.reminded_count} parent${plural(data.reminded_count)}`,
                data.invited_count ? `invited ${data.invited_count} who hadn't been asked yet` : ''
              ].filter(Boolean).join(', '),
          ...extras
        ].join(', ') + '.');
        setActivityFor(null);
      } else {
        setError(response.error || 'Failed to send to the class');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="profile-card">
      <div className="collapsible-header" onClick={onToggle}>
        <h3><span className="collapse-chevron">{collapsed ? '▶' : '▼'}</span> Classes</h3>
        {!collapsed && (
          <span style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
            <button
              onClick={(e) => { e.stopPropagation(); openFindClass(); }}
              className="add-btn"
            >
              Find my child's class
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setShowCreate(true); setError(null); }}
              className="add-btn"
            >
              + New Class
            </button>
          </span>
        )}
      </div>

      {!collapsed && (
        <>
          {error && <div className="class-rep-banner class-rep-banner-error">{error}</div>}
          {notice && <div className="class-rep-banner class-rep-banner-success">{notice}</div>}

          {memberships.length > 0 && (
            <div className="class-rep-members" style={{ marginBottom: '16px' }}>
              <div className="class-rep-subhead">Your children's classes</div>
              {memberships.map((membership: any) => (
                <div key={membership.member_uuid} className="class-rep-member">
                  <div>
                    <div className="class-rep-member-name">
                      {membership.child_name} · {membership.class_name}
                    </div>
                    <div className="class-rep-member-parent">
                      {[
                        membership.school_name,
                        (membership.reps || []).length > 0
                          ? `Rep${membership.reps.length === 1 ? '' : 's'}: ${membership.reps.map((r: any) => r.name).join(', ')}`
                          : 'No rep listed'
                      ].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  <div className="class-rep-member-actions">
                    <span className={`class-rep-status ${membership.status === 'accepted' ? 'connected' : membership.status === 'declined' ? 'declined' : 'pending'}`}>
                      {membership.status === 'accepted'
                        ? 'In the class'
                        : membership.status === 'requested'
                          ? 'Waiting on the rep'
                          : membership.status === 'declined'
                            ? 'Declined'
                            : 'Invited'}
                    </span>
                    <button
                      onClick={() => handleLeaveClass(membership)}
                      className="class-rep-remove-btn"
                      title={`Take ${membership.child_name} out of ${membership.class_name}`}
                      disabled={busy}
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {loading ? (
            <div className="empty-state"><p>Loading your classes...</p></div>
          ) : classes.length === 0 ? (
            memberships.length === 0 ? (
              <div className="empty-state">
                <p>No classes yet</p>
                <p>Find the class your child is already in and ask to join — or create one, add the children in it, and invite or remind every parent at once.</p>
              </div>
            ) : null
          ) : (
            <div className="parents-list">
              <div className="class-rep-subhead">Classes you rep</div>
              {classes.map((group) => (
                <div key={group.uuid} className="class-rep-card">
                  <div className="class-rep-card-header">
                    <div>
                      <h4>{group.name}</h4>
                      <p className="class-rep-card-meta">
                        {[group.school_name, group.year_group].filter(Boolean).join(' · ') || 'No school set'}
                        {group.my_child_name ? ` · ${group.my_child_name}'s class` : ''}
                      </p>
                      <p className="class-rep-card-meta">
                        {group.members.length + (group.reps || []).length} child
                        {group.members.length + (group.reps || []).length === 1 ? '' : 'ren'}
                      </p>
                    </div>
                    <button
                      onClick={() => handleDeleteClass(group)}
                      className="class-rep-delete-btn"
                    >
                      Delete
                    </button>
                  </div>

                  <div className="class-rep-members">
                    <div className="class-rep-subhead">Reps</div>
                    {(group.reps || []).map((rep) => (
                      <div key={rep.uuid} className="class-rep-member">
                        <div>
                          <div className="class-rep-member-name">{rep.parent_name}</div>
                          <div className="class-rep-member-parent">
                            {rep.child_name ? `${rep.child_name} · ` : ''}{rep.parent_email}
                          </div>
                        </div>
                        <div className="class-rep-member-actions">
                          <span className={`class-rep-status ${rep.status === 'accepted' ? 'connected' : rep.status === 'declined' ? 'declined' : 'pending'}`}>
                            {rep.status === 'accepted' ? 'Rep' : rep.status === 'declined' ? 'Declined' : 'Asked'}
                          </span>
                          <button
                            onClick={() => handleRemoveRep(group, rep)}
                            className="class-rep-remove-btn"
                            title={`Remove ${rep.parent_name}`}
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {(group.members.length > 0 || (group.reps || []).length > 0) && (
                    <div className="class-rep-members">
                      <div className="class-rep-subhead">Children</div>
                      {(group.reps || [])
                        .filter((rep) => rep.child_name && rep.status === 'accepted')
                        .map((rep) => (
                          <div key={`rep-child-${rep.uuid}`} className="class-rep-member">
                            <div>
                              <div className="class-rep-member-name">{rep.child_name}</div>
                              <div className="class-rep-member-parent">{rep.parent_name} · {rep.parent_email}</div>
                            </div>
                            <div className="class-rep-member-actions">
                              <span className="class-rep-status connected">Rep's child</span>
                            </div>
                          </div>
                        ))}
                      {group.members.map((member) => {
                        const status = memberStatus(member);
                        return (
                          <div key={member.uuid} className="class-rep-member">
                            <div>
                              <div className="class-rep-member-name">{member.child_name}</div>
                              <div className="class-rep-member-parent">{member.parent_name} · {member.parent_email}</div>
                            </div>
                            <div className="class-rep-member-actions">
                              {member.status === 'requested' ? (
                                <>
                                  <button
                                    className="class-rep-add-btn"
                                    disabled={busy}
                                    onClick={() => handleJoinRequestResponse(group, member, 'accept')}
                                  >
                                    Let in
                                  </button>
                                  <button
                                    className="class-rep-cancel-btn class-rep-inline-btn"
                                    disabled={busy}
                                    onClick={() => handleJoinRequestResponse(group, member, 'decline')}
                                  >
                                    Decline
                                  </button>
                                </>
                              ) : (
                                <>
                                  <span className={`class-rep-status ${status.tone}`}>{status.label}</span>
                                  <button
                                    onClick={() => handleRemoveMember(group, member)}
                                    className="class-rep-remove-btn"
                                    title={`Remove ${member.child_name}`}
                                  >
                                    ×
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div className="class-rep-actions">
                    <button className="add-btn" onClick={() => { setAddMode('member'); setAddMemberFor(group); setError(null); resetMemberSearch(); }}>+ Add Child</button>
                    <button className="add-btn" onClick={() => { setAddMode('rep'); setAddMemberFor(group); setError(null); resetMemberSearch(); }}>+ Add Rep</button>
                    <button className="add-btn" onClick={() => openActivityPicker(group, 'invite')}>Invite to Activity</button>
                    <button className="add-btn" onClick={() => openActivityPicker(group, 'remind')}>Send Reminder</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {showCreate && (
        <div className="modal-overlay">
          <div className="modal" onKeyDown={(e) => { if (e.key === 'Escape') setShowCreate(false); }}>
            <div className="modal-header">
              <h3>New Class</h3>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setShowCreate(false)}
                aria-label="Close modal"
              >
                ×
              </button>
            </div>

            <div className="form-group">
              <label>Class Name</label>
              <input
                type="text"
                className="form-input"
                placeholder="e.g. 4B"
                value={newClassName}
                onChange={(e) => setNewClassName(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>School</label>
              <input
                type="text"
                className="form-input"
                placeholder="Optional"
                value={newSchoolName}
                onChange={(e) => setNewSchoolName(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Year Group</label>
              <input
                type="text"
                className="form-input"
                placeholder="Optional"
                value={newYearGroup}
                onChange={(e) => setNewYearGroup(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Your Child in This Class</label>
              <select
                className="form-input"
                value={newOwnerChildUuid}
                onChange={(e) => setNewOwnerChildUuid(e.target.value)}
              >
                <option value="">Choose a child</option>
                {myChildren.map((child) => (
                  <option key={child.uuid} value={child.uuid}>{child.name}</option>
                ))}
              </select>
            </div>

            <div className="modal-actions">
              <button className="class-rep-cancel-btn" onClick={() => setShowCreate(false)} disabled={busy}>
                Cancel
              </button>
              <button
                className="class-rep-confirm-btn"
                onClick={handleCreateClass}
                disabled={busy}
              >
                {busy ? 'Creating...' : 'Create Class'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showFindClass && (
        <div className="modal-overlay">
          <div className="modal" onKeyDown={(e) => { if (e.key === 'Escape') setShowFindClass(false); }}>
            <div className="modal-header">
              <h3>Find your child's class</h3>
              <button type="button" className="modal-close-btn" onClick={() => setShowFindClass(false)} aria-label="Close modal">×</button>
            </div>

            <p className="class-rep-modal-hint">
              We'll look for classes at your child's school with a name close to theirs, and show you who reps them.
            </p>

            {error && <div className="class-rep-banner class-rep-banner-error">{error}</div>}

            <div className="form-group">
              <label>Your Child</label>
              <select
                className="form-input"
                value={findChildUuid}
                onChange={(e) => {
                  const child: any = myChildren.find(c => c.uuid === e.target.value);
                  setFindChildUuid(e.target.value);
                  if (child?.school) setFindSchool(child.school);
                  if (child?.grade) setFindClassName(child.grade);
                }}
              >
                <option value="">Choose a child</option>
                {myChildren.map((child) => (
                  <option key={child.uuid} value={child.uuid}>{child.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>School</label>
              <input
                type="text"
                className="form-input"
                placeholder="e.g. Berkhamsted"
                value={findSchool}
                onChange={(e) => setFindSchool(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Class Name</label>
              <input
                type="text"
                className="form-input"
                placeholder="e.g. 4B"
                value={findClassName}
                onChange={(e) => setFindClassName(e.target.value)}
              />
            </div>

            {findResults && (
              findResults.length === 0 ? (
                <p className="class-rep-modal-hint">
                  No classes close to that. Check the spelling, or create the class yourself and invite the other parents.
                </p>
              ) : (
                <div className="class-rep-results">
                  {findResults.map((group: any) => (
                    <div key={group.uuid} className="class-rep-result-card">
                      <div className="class-rep-result-parent">
                        <div>
                          <h5>{group.name}{group.school_name ? ` · ${group.school_name}` : ''}</h5>
                          <div className="class-rep-card-meta">
                            {(group.reps || []).length > 0
                              ? `Rep${group.reps.length === 1 ? '' : 's'}: ${group.reps.join(', ')}`
                              : 'No rep listed'}
                            {' · '}{group.child_count} child{group.child_count === 1 ? '' : 'ren'}
                            {group.my_status === 'pending' && !group.i_rep_it && ' · you have been invited'}
                          </div>
                        </div>
                        {group.my_status === 'pending' && !group.i_rep_it ? (
                          <button
                            className="class-rep-add-btn"
                            disabled={busy}
                            onClick={() => handleAcceptFoundInvitation(group)}
                          >
                            Accept invite
                          </button>
                        ) : (
                          <button
                            className="class-rep-add-btn"
                            disabled={busy || !!group.my_status || group.i_rep_it}
                            onClick={() => handleRequestToJoin(group)}
                          >
                            {group.i_rep_it
                              ? 'You rep this'
                              : group.my_status === 'accepted'
                                ? 'Already in'
                                : group.my_status === 'requested'
                                  ? 'Waiting on the rep'
                                  : group.my_status === 'declined'
                                    ? 'Declined'
                                    : 'Ask to join'}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}

            <div className="modal-actions">
              <button className="class-rep-cancel-btn" onClick={() => setShowFindClass(false)} disabled={busy}>
                Close
              </button>
              <button className="class-rep-confirm-btn" onClick={handleFindClasses} disabled={finding}>
                {finding ? 'Searching...' : 'Search'}
              </button>
            </div>
          </div>
        </div>
      )}

      {addMemberFor && (
        <div className="modal-overlay">
          <div className="modal" onKeyDown={(e) => { if (e.key === 'Escape') closeAddMember(); }}>
            <div className="modal-header">
              <h3>{addMode === 'rep' ? `Add a Rep to ${addMemberFor.name}` : `Add a Child to ${addMemberFor.name}`}</h3>
              <button
                type="button"
                className="modal-close-btn"
                onClick={closeAddMember}
                aria-label="Close modal"
              >
                ×
              </button>
            </div>

            <p className="class-rep-modal-hint">
              {addMode === 'rep'
                ? "Search for the parent by email or phone. They'll be asked to help run the class, and can invite and remind it like you."
                : "Search for the parent by email or phone, then pick their child. If they're not on Cluubz yet we'll hold a place for them."}
            </p>

            {error && <div className="class-rep-banner class-rep-banner-error">{error}</div>}
            {notice && <div className="class-rep-banner class-rep-banner-success">{notice}</div>}

            {/* Same search as the Connections screen */}
            <div className="search-method-toggle" style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
              <button
                className={`method-btn ${searchMethod === 'email' ? 'active' : ''}`}
                onClick={() => { setSearchMethod('email'); resetMemberSearch(); }}
                style={{
                  flex: 1,
                  padding: '10px',
                  border: 'none',
                  borderRadius: '8px',
                  background: searchMethod === 'email' ? 'linear-gradient(135deg, #667eea, #764ba2)' : '#e9ecef',
                  color: searchMethod === 'email' ? 'white' : '#6c757d',
                  fontWeight: searchMethod === 'email' ? 'bold' : 'normal',
                  cursor: 'pointer'
                }}
              >
                Email
              </button>
              <button
                className={`method-btn ${searchMethod === 'phone' ? 'active' : ''}`}
                onClick={() => { setSearchMethod('phone'); resetMemberSearch(); }}
                style={{
                  flex: 1,
                  padding: '10px',
                  border: 'none',
                  borderRadius: '8px',
                  background: searchMethod === 'phone' ? 'linear-gradient(135deg, #667eea, #764ba2)' : '#e9ecef',
                  color: searchMethod === 'phone' ? 'white' : '#6c757d',
                  fontWeight: searchMethod === 'phone' ? 'bold' : 'normal',
                  cursor: 'pointer'
                }}
              >
                Phone
              </button>
            </div>

            <div className="search-form">
              {searchMethod === 'email' ? (
                <input
                  type="email"
                  placeholder="Enter email address"
                  value={searchEmail}
                  onChange={(e) => { setSearchEmail(e.target.value); resetMemberSearch(); }}
                  className="search-input"
                />
              ) : (
                <div style={{ flex: 1 }}>
                  <PhoneInput
                    value={searchPhone}
                    onChange={(value: string) => { setSearchPhone(value); resetMemberSearch(); }}
                    placeholder="Enter phone number"
                    className="search-phone-input"
                  />
                </div>
              )}
              <button
                onClick={handleSearchParent}
                disabled={searching}
                className={`search-btn ${searching ? 'disabled' : ''}`}
              >
                {searching ? 'Searching...' : 'Search'}
              </button>
            </div>

            {searchResults.length > 0 && (
              <div className="class-rep-results">
                {searchResults.map((parent: any) => {
                  const parentChildren = (parent.children || []).filter((c: any) => !c.is_parent);
                  return (
                    <div key={parent.user_uuid} className="class-rep-result-card">
                      <div className="class-rep-result-parent">
                        <div>
                          <h5>{parent.username}</h5>
                          <div className="class-rep-card-meta">
                            {addMode === 'rep'
                              ? (parentChildren.map((c: any) => c.name).join(', ') || 'No children listed')
                              : `${parentChildren.length} child${parentChildren.length !== 1 ? 'ren' : ''}`}
                          </div>
                        </div>
                        {addMode === 'rep' && (() => {
                          const existingRep = (addMemberFor.reps || []).find(r => r.parent_uuid === parent.user_uuid);
                          return (
                            <button
                              className="class-rep-add-btn"
                              onClick={() => handleAddRep(parent.user_uuid, parent.username)}
                              disabled={busy || !!existingRep}
                            >
                              {existingRep
                                ? (existingRep.status === 'accepted' ? 'Already a rep' : 'Asked')
                                : 'Add as rep'}
                            </button>
                          );
                        })()}
                      </div>
                      {addMode === 'rep' ? null : parentChildren.length === 0 ? (
                        <p className="class-rep-modal-hint">This parent has no children on their account yet.</p>
                      ) : (
                        <div className="class-rep-members">
                          {parentChildren.map((child: any) => {
                            const alreadyIn = addMemberFor.members.some(m => m.child_uuid === child.uuid);
                            const label = alreadyIn ? 'In class' : 'Add';
                            return (
                              <div key={child.uuid} className="class-rep-member">
                                <div>
                                  <div className="class-rep-member-name">{child.name}</div>
                                  {child.grade && <div className="class-rep-member-parent">{child.grade}</div>}
                                </div>
                                <button
                                  className="class-rep-add-btn"
                                  onClick={() => handleAddFoundChild(child.uuid, child.name)}
                                  disabled={busy || alreadyIn}
                                >
                                  {label}
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {searchedFor && searchResults.length === 0 && addMode === 'rep' ? (
              <p className="class-rep-modal-hint">
                Nobody on Cluubz matches that. A rep needs their own Cluubz account before they can help run the class.
              </p>
            ) : searchedFor && searchResults.length === 0 && (
              searchMethod === 'phone' ? (
                <p className="class-rep-modal-hint">
                  Nobody on Cluubz uses that number. Search by email instead to hold a place for them.
                </p>
              ) : (
                <>
                  <p className="class-rep-modal-hint">
                    {searchedFor} isn't on Cluubz yet — add their child anyway and we'll connect them when they join.
                  </p>

                  <div className="form-group">
                    <label>Child's Name</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Enter the child's name"
                      value={memberChildName}
                      onChange={(e) => setMemberChildName(e.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <label>Parent's Name</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Optional"
                      value={memberParentName}
                      onChange={(e) => setMemberParentName(e.target.value)}
                    />
                  </div>
                </>
              )
            )}

            <div className="modal-actions">
              {showHoldAPlace ? (
                <>
                  <button className="class-rep-cancel-btn" onClick={closeAddMember} disabled={busy}>
                    Cancel
                  </button>
                  <button
                    className="class-rep-confirm-btn"
                    onClick={handleAddNewChild}
                    disabled={busy}
                  >
                    {busy ? 'Adding...' : 'Add Child'}
                  </button>
                </>
              ) : (
                <button className="class-rep-confirm-btn" onClick={closeAddMember} disabled={busy}>
                  Done
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {activityFor && (
        <div className="modal-overlay">
          <div className="modal" onKeyDown={(e) => { if (e.key === 'Escape') setActivityFor(null); }}>
            <div className="modal-header">
              <h3>
                {activityFor.mode === 'invite' ? 'Invite' : 'Remind'} {activityFor.group.name}
              </h3>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setActivityFor(null)}
                aria-label="Close modal"
              >
                ×
              </button>
            </div>

            {error && <div className="class-rep-banner class-rep-banner-error">{error}</div>}
            {notice && <div className="class-rep-banner class-rep-banner-success">{notice}</div>}

            {loadingActivities ? (
              <p className="class-rep-modal-hint">Loading activities...</p>
            ) : activities.length === 0 ? (
              <p className="class-rep-modal-hint">
                No upcoming activities for {activityFor.group.my_child_name || 'your child'}. Create one first, then come back here to share it with the class.
              </p>
            ) : (
              <>
                <div className="form-group">
                  <label>Activity</label>
                  <select
                    className="form-input"
                    value={selectedActivityUuid}
                    onChange={(e) => setSelectedActivityUuid(e.target.value)}
                  >
                    <option value="">Choose an activity</option>
                    {activities.map((activity) => (
                      <option key={activity.uuid} value={activity.uuid}>
                        {activity.name}{activity.start_date ? ` — ${formatActivityDate(activity.start_date)}` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>{activityFor.mode === 'invite' ? 'Message' : 'Reminder Wording'}</label>
                  <textarea
                    className="form-input class-rep-textarea"
                    placeholder="Optional"
                    value={activityMessage}
                    onChange={(e) => setActivityMessage(e.target.value)}
                  />
                </div>
              </>
            )}

            <div className="modal-actions">
              <button className="class-rep-cancel-btn" onClick={() => setActivityFor(null)} disabled={busy}>
                Cancel
              </button>
              <button
                className="class-rep-confirm-btn"
                onClick={handleSendToClass}
                disabled={busy || !selectedActivityUuid}
              >
                {busy ? 'Sending...' : activityFor.mode === 'invite' ? 'Invite Class' : 'Send Reminder'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ClassRepSection;
